import { useWindowDimensions } from 'react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
export const MAIN_CONTAINER_WIDTH_XL = 1240; // 1440 - navbar width 200px
export var BREAKPOINTS;
(function (BREAKPOINTS) {
    BREAKPOINTS["XS"] = "XS";
    BREAKPOINTS["S"] = "S";
    BREAKPOINTS["M"] = "M";
    BREAKPOINTS["L"] = "L";
    BREAKPOINTS["XL"] = "XL";
})(BREAKPOINTS || (BREAKPOINTS = {}));
const breakpointsArr = Object.values(BREAKPOINTS);
export const useBreakpoints = (debounceTime = 60) => {
    const { width, height } = useWindowDimensions();
    const debouncedResize = useRef();
    const [debouncedWidth, setDebouncedWidth] = useState(width);
    const [debouncedHeight, setDebouncedHeight] = useState(height);
    const handleResize = useCallback((width, height) => {
        if (width !== debouncedWidth) {
            setDebouncedWidth(width);
        }
        if (height !== debouncedHeight) {
            setDebouncedHeight(height);
        }
    }, [debouncedHeight, debouncedWidth]);
    debouncedResize.current = debounce(handleResize, debounceTime);
    useEffect(() => {
        var _a;
        (_a = debouncedResize.current) === null || _a === void 0 ? void 0 : _a.call(debouncedResize, width, height);
        return () => {
            var _a;
            (_a = debouncedResize.current) === null || _a === void 0 ? void 0 : _a.cancel();
        };
    }, [width, height, debounceTime]);
    return useMemo(() => {
        const LESS_THAN_S = debouncedWidth <= 768;
        const LESS_THAN_M = debouncedWidth <= 1024;
        const LESS_THAN_L = debouncedWidth <= 1440;
        const MORE_THAN_XS = debouncedWidth > 393;
        const MORE_THAN_S = debouncedWidth > 768;
        const MORE_THAN_M = debouncedWidth > 1024;
        const MORE_THAN_L = debouncedWidth > 1440;
        const MORE_THAN_OR_EQUAL_L = debouncedWidth >= 1440;
        const XS = debouncedWidth <= 393;
        const S = MORE_THAN_XS && LESS_THAN_S;
        const M = MORE_THAN_S && LESS_THAN_M;
        const L = MORE_THAN_M && LESS_THAN_L;
        const XL = MORE_THAN_L;
        const current = ((XL && 'XL') ||
            (L && 'L') ||
            (M && 'M') ||
            (S && 'S') ||
            'XS');
        const currentIndex = breakpointsArr.indexOf(current);
        return {
            breakpoints: {
                XS,
                S,
                M,
                L,
                XL,
                LESS_THAN_S,
                LESS_THAN_M,
                LESS_THAN_L,
                MORE_THAN_XS,
                MORE_THAN_S,
                MORE_THAN_M,
                MORE_THAN_L,
                MORE_THAN_OR_EQUAL_L,
                current,
                prev: currentIndex > 0 ? breakpointsArr[currentIndex - 1] : current,
                next: currentIndex < breakpointsArr.length
                    ? breakpointsArr[currentIndex + 1]
                    : current,
            },
            width: debouncedWidth,
            height: debouncedHeight,
        };
    }, [debouncedHeight, debouncedWidth]);
};
